/* eslint-disable react/prop-types */
import React, { Suspense, lazy, useEffect } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';

import { Spin } from 'antd';

import SignUpPage from 'pages/onboarding/SignUpPage';
import ForgotPasswordPage from 'pages/forgot-password/ForgotPasswordPage';
import ResetPasswordPage from 'pages/reset-password/ResetPasswordPage';
import GetStartedPage from 'pages/get-started/GetStartedPage';
import CoHostSetPasswordPage from 'pages/CoHostSetPasswordPage';
import CheckoutPage from 'pages/CheckoutPage';
import CartDetailsPage from 'pages/CartDetailsPage';
import PurchaseRequestPage from 'pages/manage-request/PurchaseRequestPage';
import PurchaseRequestConfirmation from 'pages/manage-request/PurchaseRequstConfirmation';
import CheckoutDetailsPage from 'pages/CheckoutDetailsPage';
import OnboardingVideoPage from 'pages/OnboardingVideoPage';
import StoreListPage from 'components/StoreList';
import PayoutsPage from 'components/Dashboard/Payouts';
import SettingsAccountPage from 'pages/settings/Account';
import RequestListPage from 'components/RequestList';
import OrdersListPage from 'pages/OrdersList';
import ServicesCalendarPage from 'pages/ServicesCalendar';
import DashboardStorePage from 'pages/dashboard-store/DashboardStorePage';
import SignagePage from 'pages/signage/SignagePage';
import SalesPage from 'components/Dashboard/Sales';
import PurchaseDetails from 'pages/PurchaseDetails';

import GuestStorePage from 'components/Store/Store';

import PartnersPage from 'components/Partners/Partners';
import PartnerLandingPage from 'components/Partners/PartnerLanding/PartnerLanding';
import IntegrationPage from 'pages/IntegrationPage';

import { LoginGuard } from './LoginGuard';
import { GuestLoginGuard } from './GuestLoginGuard';

const LoginPage = lazy(() =>
  import(
    /* webpackChunkName: "store" */
    'components/LoginPage'
  ),
);

const Store = lazy(() =>
  import(
    /* webpackChunkName: "store" */
    'components/Store/Store'
  ),
);

const OnboardingPage = lazy(() =>
  import(
    /* webpackChunkName: "store" */
    'pages/onboarding/OnboardingPage'
  ),
);

const LocalProductSearchPage = lazy(() =>
  import(
    /* webpackChunkName: "store" */
    'pages/local-product-search/LocalProductSearchPage'
  ),
);

const SubscriptionPage = lazy(() =>
  import(
    /* webpackChunkName: "store" */
    'pages/subscription/SubscriptionPage.container'
  ),
);
const AddOnPage = lazy(() =>
  import(
    /* webpackChunkName: "store" */
    'pages/subscription/AddOnPage.container'
  ),
);

export function Routes() {
  const { search } = useLocation();
  const location = useLocation();
  const background = location.state && location.state.background;

  useEffect(() => {
    setTimeout(() => window.scroll({ top: 0, behavior: 'smooth' }), 10);
  }, [location.pathname]);

  useEffect(() => {
    const nextParam = new URLSearchParams(search).get('next');
    if (nextParam) {
      localStorage.setItem('nextUrl', nextParam);
    }
  }, []);

  return (
    <Suspense fallback={<Spin />}>
      <Switch location={background ?? location}>
        {/* Appending trailing slashes to all the routes */}
        <Route
          path="/:url*"
          exact
          strict
          render={(props) =>
            props.location.search ? (
              <Redirect
                to={{
                  pathname: `${props.location.pathname}${props.location.search}/`,
                  state: props.location.state,
                }}
              />
            ) : (
              <Redirect
                to={{
                  pathname: `${props.location.pathname}/`,
                  state: props.location.state,
                }}
              />
            )
          }
        />
        {/* ================ PUBLIC allow any ================= */}
        <Redirect from="/" to="/login/" exact />

        <Route path="/get-started/" component={GetStartedPage} />
        <Route path="/co-host/setup-password/:invitationUUID/" component={CoHostSetPasswordPage} />
        <Route path="/login/" component={LoginPage} />
        <Route path="/forgot-password/" component={ForgotPasswordPage} />
        <Route path="/reset-password/:token?" component={ResetPasswordPage} />
        <Route path="/signup/" component={SignUpPage} />
        <Route path="/pricing/" component={SubscriptionPage} />
        <Route path="/add-on/" component={AddOnPage} />
        {/* TODO: move the url in URLS */}
        <Route
          path="/:actorType(owner|guest|merchant)/:action(approve|deny|cancel)"
          component={PurchaseRequestConfirmation}
        />
        {/* TODO: move the url in URLS */}
        <Route
          path="/:actorType(owner|guest|merchant)/:purchaseUUID"
          component={PurchaseRequestPage}
        />
        <Route path="/vendors/" component={LocalProductSearchPage} />
        <Route path="/new-store/">
          <Redirect to="/signup/" />
        </Route>
        {/* ==================================================== */}

        {/* ================ OWNER [HOST] pages ================= */}
        {/* Owner/Host Pages for one store */}
        <LoginGuard path="/dashboard/store/:storeId/">
          <Switch>
            <Route path="/dashboard/store/:storeId/" exact component={DashboardStorePage} />
            <Route
              path="/dashboard/store/:storeId/products/:category/"
              component={DashboardStorePage}
            />
            <Route
              path="/dashboard/store/:storeId/sales/:purchaseId/"
              component={PurchaseDetails}
            />
            <Route path="/dashboard/store/:storeId/sales/" component={SalesPage} />
            <Route path="/dashboard/store/:storeId/signage/" component={SignagePage} />
          </Switch>
        </LoginGuard>
        {/* ==================================================== */}
        {/* OWNER OVERVIEW ROUTES */}
        <LoginGuard path="/overview/">
          <Switch>
            <Route path="/overview/stores/" component={StoreListPage} />
            <Route path="/overview/payouts/" component={PayoutsPage} />
            <Route path="/overview/account/:tab/" component={SettingsAccountPage} />
            <Route path="/overview/requests/" component={RequestListPage} />
            <Route path="/overview/orders/" component={OrdersListPage} />
            <Route path="/overview/services-calendar/" component={ServicesCalendarPage} />
            <Route path="/overview/partners/">
              <Switch>
                <Route path="/overview/partners/" exact component={PartnersPage} />
                <Route path="/overview/partners/landing/:company/" component={PartnerLandingPage} />
                <Route path="/overview/partners/:company/" component={IntegrationPage} />
              </Switch>
            </Route>
          </Switch>
        </LoginGuard>

        <LoginGuard path="/onboarding/">
          <Switch>
            <Route path="/onboarding/" exact component={OnboardingPage} />
            <Route path="/onboarding/onboarding-video/" component={OnboardingVideoPage} />
          </Switch>
        </LoginGuard>

        {/* ==================================================== */}
        {/* ================ GUEST pages ================= */}
        <GuestLoginGuard path="/store/:storeId/">
          <Switch>
            <Route path="/store/:storeId/cart/" component={CartDetailsPage} />
            <Route path="/store/:storeId/checkout/:orderId/:customerId/" component={CheckoutPage} />
            <Route
              path="/store/:storeId/:orderId/checkout-details/"
              component={CheckoutDetailsPage}
            />
            <Route path="/store/:storeId/" component={GuestStorePage} />
            <Route path="/store/:storeId/:category" exact component={Store} />
          </Switch>
        </GuestLoginGuard>
        <Redirect from="/:storeId/" to="/store/:storeId/" />
        {/* ==================================================== */}
      </Switch>
    </Suspense>
  );
}
